import type { HermesAPI } from "./api";
import { HermesError } from "./errors";
import { arr, bool, int, num, obj, s, str, type Json } from "./json";
import { isDataURL, mediaRefsIn, mediaRefsInToolResult, type MediaKind, type MediaRef } from "./media";

// MARK: 会话搜索与消息（作品区用）

export interface SessionSearchHit {
  session_id: string;
  title?: string;
  source?: string;
  last_active?: number;
  session_started?: number;
}

/** 全文搜索会话（Hermes 的 FTS），按会话去重。profile 为空 = 默认 Bot */
export async function searchSessions(api: HermesAPI, query: string, limit = 50, profile?: string): Promise<SessionSearchHit[]> {
  const raw = obj(await api.client.broker.getJSON("/api/sessions/search", { q: query, limit: String(limit), profile }));
  return (arr(raw?.results) ?? []).map(obj).filter(Boolean).flatMap((h) => {
    const id = s(h!.session_id);
    return id ? [{ session_id: id, title: s(h!.title), source: s(h!.source), last_active: num(h!.last_active), session_started: num(h!.session_started) }] : [];
  });
}

/** 一个会话的原始消息（最近 500 条） */
export async function sessionMessagesRaw(api: HermesAPI, sessionID: string, profile?: string): Promise<Json[]> {
  return arr(obj(await api.client.broker.getJSON(`/api/sessions/${encodeURIComponent(sessionID)}/messages`, { profile }))?.messages) ?? [];
}

// MARK: 作品

/** 对话里产出的一个文件 */
export interface AssetItem {
  source: string;
  kind: MediaKind;
  sessionID: string;
  sessionTitle?: string;
  origin?: string;
  /** Unix 秒 */
  timestamp: number;
}

export function assetRef(a: AssetItem): MediaRef {
  return { source: a.source, kind: a.kind };
}

function messageText(m: Json): string {
  const o = obj(m);
  const content = o?.content;
  if (typeof content === "string") return content;
  const parts = arr(content);
  if (parts) return parts.map((p) => str(obj(p)?.text) ?? str(p) ?? "").filter(Boolean).join("\n");
  return "";
}

/** 从一个会话的原始消息里找出产出：助手回复里的 MEDIA:、生成类工具的结果。用户自己发的图不算。 */
export function scanAssets(messages: Json[], sessionID: string, title: string | undefined, origin: string | undefined): AssetItem[] {
  const out: AssetItem[] = [];
  const seen = new Set<string>();
  for (const m of messages) {
    const o = obj(m);
    const role = str(o?.role) ?? "";
    const ts = num(o?.timestamp) ?? 0;
    let refs: MediaRef[] = [];
    if (role === "assistant") {
      refs = mediaRefsIn(messageText(m));
    } else if (role === "tool") {
      let json: Json | undefined;
      try { json = JSON.parse(messageText(m)); } catch { json = undefined; }
      if (json !== undefined) refs = mediaRefsInToolResult(json).filter((r) => r.kind === "image" || r.kind === "video");
    } else {
      continue;
    }
    for (const ref of refs) {
      if (isDataURL(ref) || seen.has(ref.source)) continue;
      seen.add(ref.source);
      out.push({ source: ref.source, kind: ref.kind, sessionID, sessionTitle: title, origin, timestamp: ts });
    }
  }
  return out;
}

/** 服务器上一个目录里的文件名（作品区用来判断文件是否已被删除）。目录不存在返回空。 */
export async function listDirectory(api: HermesAPI, path: string, profile?: string): Promise<Set<string>> {
  const raw = obj(await api.client.broker.getJSON("/api/fs/list", { path, profile }));
  const error = str(raw?.error);
  // 目录不存在（ENOENT）= 里面的文件都没了；没权限等其它错误说明不了文件在不在
  if (error && error !== "ENOENT") throw HermesError.transport(`列不出目录：${error}`);
  return new Set((arr(raw?.entries) ?? []).map((e) => str(obj(e)?.name)).filter((n): n is string => !!n));
}

// MARK: 自动化成果

/**
 * 定时任务的一次运行（会话列表 /api/sessions?source=cron 里的一行）。
 * Hermes 的定时任务每次运行都是一个 source=cron 的会话（id 是 cron_<任务id>_<时间>，标题“任务名 · Oct 11 06:33”）。
 */
export interface AutomationRun {
  id: string;
  title?: string;
  started_at?: number;
  last_active?: number;
  is_active?: boolean;
  tool_call_count?: number;
}

/** 任务名：标题里“ · ”前面那段 */
export function runJobName(r: AutomationRun): string {
  if (!r.title) return "定时任务";
  const i = r.title.lastIndexOf(" · ");
  return i >= 0 ? r.title.slice(0, i) : r.title;
}

/** 最近的定时任务运行，新的在前（所有任务一起） */
export async function recentAutomationRuns(api: HermesAPI, profile?: string, limit = 20): Promise<AutomationRun[]> {
  const raw = obj(await api.client.broker.getJSON("/api/sessions", { source: "cron", order: "recent", limit: String(limit), profile }));
  return (arr(raw?.sessions) ?? []).map(obj).filter(Boolean).flatMap((x) => {
    const id = s(x!.id);
    return id ? [{ id, title: s(x!.title), started_at: num(x!.started_at), last_active: num(x!.last_active), is_active: bool(x!.is_active), tool_call_count: int(x!.tool_call_count) }] : [];
  });
}

/** 一段对话最后一条助手回复的文字（只取最后几条消息，不拉整段对话） */
export async function lastReply(api: HermesAPI, sessionID: string, profile?: string): Promise<string | undefined> {
  const raw = obj(await api.client.broker.getJSON(`/api/sessions/${encodeURIComponent(sessionID)}/messages`, { limit: "4", order: "latest", profile }));
  const replies = (arr(raw?.messages) ?? []).map(obj).filter((m) => str(m?.role) === "assistant" && (str(m?.content) ?? "") !== "");
  // order=latest 返回的顺序在不同版本里不一定一样：按时间戳挑最后一条助手回复
  replies.sort((a, b) => (num(a?.timestamp) ?? 0) - (num(b?.timestamp) ?? 0));
  return str(replies[replies.length - 1]?.content);
}

/** 立即运行一次定时任务（网页后台同一个接口） */
export async function triggerCronJob(api: HermesAPI, id: string, profile?: string): Promise<void> {
  await api.client.broker.sendJSON("POST", `/api/cron/jobs/${encodeURIComponent(id)}/trigger`, {}, { profile });
}

/** 回复的第一段有内容的文字，去掉 Markdown 记号 */
export function replySummary(reply: string): string {
  let text = reply.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1").replace(/[*_`>#|]+/g, " ");
  const line = text.split(/\n/).map((l) => l.trim()).find((l) => l && l !== "-" && !/^[-=—]+$/.test(l)) ?? "";
  text = line.replace(/\s+/g, " ");
  if (text.toUpperCase().startsWith("[SILENT]")) return "（这次没有需要汇报的内容）";
  return [...text].slice(0, 140).join("");
}

// MARK: cron 表达式 → 人话

/** “0 9 * * *” → “每天 09:00”。认不出的原样显示。 */
export function describeSchedule(raw?: string): string {
  const v = raw?.trim();
  if (!v) return "-";
  const lower = v.toLowerCase();
  if (lower.startsWith("every ")) {
    const rest = lower.slice(6).trim();
    const n = rest.slice(0, -1).trim();
    switch (rest[rest.length - 1]) {
      case "m": return `每 ${n} 分钟`;
      case "h": return `每 ${n} 小时`;
      case "d": return `每 ${n} 天`;
      default: return v;
    }
  }
  const f = v.split(/\s+/);
  const minute = f.length === 5 && /^\d+$/.test(f[0]) ? parseInt(f[0], 10) : undefined;
  if (minute === undefined) return v;
  const weekdays = ["日", "一", "二", "三", "四", "五", "六", "日"];
  const time = (h: string) => (/^\d+$/.test(h) ? `${String(parseInt(h, 10)).padStart(2, "0")}:${String(minute).padStart(2, "0")}` : undefined);
  if (f[1].includes(",")) {
    const hours = f[1].split(",").map(time).filter(Boolean);
    if (f[2] === "*" && f[3] === "*" && f[4] === "*" && hours.length) return "每天 " + hours.join("、");
  }
  const t = time(f[1]);
  if (!t) return f[1] === "*" && f.slice(2).every((x) => x === "*") ? `每小时第 ${minute} 分` : v;
  if (f[2] === "*" && f[3] === "*") {
    if (f[4] === "*") return `每天 ${t}`;
    if (f[4] === "1-5") return `工作日 ${t}`;
    if (f[4] === "0,6" || f[4] === "6,0") return `周末 ${t}`;
    if (/^\d+$/.test(f[4])) return `每周${weekdays[parseInt(f[4], 10) % 8]} ${t}`;
  }
  if (/^\d+$/.test(f[2]) && f[3] === "*" && f[4] === "*") return `每月 ${f[2]} 号 ${t}`;
  return v;
}

// MARK: 投递渠道

/**
 * 定时任务可选的投递渠道。Hermes 的 deliver 是逗号分隔的多个渠道（"local,weixin"），
 * 每一项可以带具体会话（"telegram:-100123"），选中判断按冒号前的平台名。
 */
export const DeliverTargets = {
  label(raw: string): string {
    return raw.split(",").map((part) => {
      const p = part.trim();
      switch (p.split(":")[0]) {
        case "local": return "TimiBot";
        case "origin": return "来源渠道";
        case "weixin": return "微信";
        case "telegram": return "Telegram";
        case "feishu": return "飞书";
        case "slack": return "Slack";
        case "discord": return "Discord";
        default: return p;
      }
    }).join(" + ");
  },
  parts: (deliver: string) => deliver.split(",").map((x) => x.trim()).filter(Boolean),
  platforms(deliver: string): string[] { return this.parts(deliver).map((p) => p.split(":")[0]); },
  contains(deliver: string, id: string): boolean { return this.platforms(deliver).includes(id); },
  /** 勾选 / 取消一个渠道；至少保留一个（全取消时回到 local） */
  toggle(deliver: string, id: string): string {
    let list = this.parts(deliver);
    if (this.contains(deliver, id)) list = list.filter((x) => x !== id && !x.startsWith(id + ":"));
    else list.push(id);
    if (!list.length) list = ["local"];
    list.sort((a, b) => (a === "local" ? 0 : 1) - (b === "local" ? 0 : 1));
    return list.join(",");
  },
  /** local（TimiBot）排第一，然后是主机上配好的消息平台；任务里已有、但列表里没有的渠道也列出来，免得被误删 */
  options(targets: { id: string; home_target_set?: boolean }[], current: string): { id: string; title: string; unavailable?: boolean }[] {
    const out: { id: string; title: string; unavailable?: boolean }[] = [{ id: "local", title: "TimiBot" }];
    for (const t of targets) {
      if (t.id === "local" || t.id.startsWith("bot_chat")) continue;
      out.push({ id: t.id, title: this.label(t.id), unavailable: t.home_target_set === false });
    }
    for (const p of this.platforms(current)) if (!out.some((o) => o.id === p)) out.push({ id: p, title: this.label(p) });
    return out;
  },
};
